/** Refused rows words: fixing them in place, downloading them, and typed corrections. */
import type { EntityKind } from "@/lib/api/imports";

export const refusedWords = {
  fixRefusedRows: "Fix refused rows",
  fixJob: "Fix this run's refused rows and send them again as a correction source, through its own dry run",
  refusedRows: "Refused rows",
  rowsIntro: "Each row is filled from what this run recorded. Fix its fields or leave it out, then read the dry run of the correction before applying it.",
  rowsToFix: "Rows to fix",
  everyRowLeftOut: "Every row left out",
  leaveOut: "Leave this row out",
  otherFields: "Other recorded fields",
  otherFieldsNote: "The source sent these as well. Clear one the row should not carry.",
  notGiven: "Not given",
  downloadRefusedRows: "Download refused rows",
  unrecorded: "This run was recorded before refused rows kept their data, so they cannot be fixed here. Download them and fix them in the file instead.",
  keepOneRow: "Keep at least one refused row to send.",
  correctedFields: "Corrected fields",
  correctedFieldsNote: "Name only what changes. A blank field keeps its standing value.",
  correctAtLeastOneField: "Correct at least one field.",
} as const;

const FIELD_LABEL: Record<string, string> = {
  name: "Name", start_date: "Start date", end_date: "End date", grades_due_date: "Grades due",
  term_key: "Term key", code: "Course code", title: "Title", credits: "Credits",
  primary_faculty_key: "Instructor key", co_instructor_faculty_keys: "Co-instructor keys, separated by ;", status: "Status",
  student_id: "Source student ID", legal_name: "Legal name", email: "Contact email",
  student_key: "Student key", course_key: "Section key", enrollment_key: "Enrollment key",
  result_letter: "Result letter", result_score: "Score, 0 to 100",
  given_name: "Given name", family_name: "Family name", delivery_email: "Delivery email",
  program_key: "Program code", cohort_key: "Cohort", admitted_year: "Admitted year",
  course_code: "Course code", term_name: "Term name", display_name: "Instructor name",
};

const KIND_LABEL: Record<EntityKind, string> = { term: "Term", faculty: "Instructor", course: "Section", student: "Learner", enrollment: "Enrollment", outcome: "Result" };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const refusedText = {
  field: (name: string) => FIELD_LABEL[name] ?? name,
  option: (value: string) => value ? `${value[0].toUpperCase()}${value.slice(1).replaceAll("_", " ")}` : refusedWords.notGiven,
  kind: (kind: EntityKind) => KIND_LABEL[kind],
  row: (kind: EntityKind, key: string) => `${KIND_LABEL[kind]} · ${key}`,
  detail: (fix: number, out: number) => `${plural(fix, "row", "rows")} to send${out ? ` · ${out} left out` : ""}`,
  parents: (n: number) => `Sent with ${plural(n, "row", "rows")} they depend on, from this run. A row this run already recorded matches by its key.`,
  required: (field: string, kind: EntityKind, key: string) => `Give the ${refusedText.field(field).toLowerCase()} for ${KIND_LABEL[kind].toLowerCase()} ${key}.`,
  number: (field: string, kind: EntityKind, key: string) => `The ${refusedText.field(field).toLowerCase()} for ${KIND_LABEL[kind].toLowerCase()} ${key} must be a number.`,
  instructor: (key: string) => `Choose a record for instructor ${key}.`,
  correctionRequired: (field: string) => `Give the ${refusedText.field(field).toLowerCase()} for this correction.`,
  correctionNumber: (field: string) => `The ${refusedText.field(field).toLowerCase()} for this correction must be a number.`,
} as const;
